import { MonthlyTimesheet, TimesheetStatus } from '../types';

/**
 * Manager / SVP Manager approval helpers. SVP Managers are modeled as OP Managers (multi-hat) and
 * approve through the same OP-Manager pages, so both stages share the visibility / action logic and
 * the `approvedByOpManagers` relation (reused as the "already approved" marker for both stages).
 */

// Statuses a Manager / SVP Manager acts on through the shared OP-Manager pages.
export const MANAGER_PENDING_STATUSES: TimesheetStatus[] = [
  TimesheetStatus.PENDING_OP_MANAGER,
  TimesheetStatus.PENDING_SVP_MANAGER,
];

export function isManagerPendingStatus(status?: MonthlyTimesheet['status']): boolean {
  return MANAGER_PENDING_STATUSES.includes(status as TimesheetStatus);
}

/**
 * Whether `ts` is awaiting THIS user's Manager/SVP approval and they have not approved it yet.
 * - Manager stage (PENDING_OP_MANAGER): the union list only contains owners this user manages, so
 *   membership is implicit — just check they haven't approved yet.
 * - SVP stage (PENDING_SVP_MANAGER): the union list can include a row via the OP-Manager OR the
 *   SVP-Manager relation, so an SVP row additionally requires the user to be one of the owner's SVP
 *   Managers (otherwise an OP Manager of the same owner would wrongly see it).
 * "Already approved" is tracked for both stages in the shared `approvedByOpManagers` relation.
 *
 * NOTE: operates on RAW timesheets (status as the TimesheetStatus enum). Do NOT pass rows that have
 * been run through `mapTsStatuses` (which replaces `status` with a display label).
 */
export function awaitsMyManagerApproval(ts: MonthlyTimesheet, userId?: number): boolean {
  if (userId == null) return false;
  const alreadyApproved = ts.approvedByOpManagers?.some((manager) => manager.id === userId) ?? false;

  if (ts.status === TimesheetStatus.PENDING_OP_MANAGER) {
    return !alreadyApproved;
  }
  if (ts.status === TimesheetStatus.PENDING_SVP_MANAGER) {
    const isSvpApprover = ts.user?.svpManagers?.some((manager) => manager.id === userId) ?? false;
    return isSvpApprover && !alreadyApproved;
  }
  return false;
}

/**
 * The status to send when approving at the row's current stage: the SVP Manager stage is the last
 * stage (closes the timesheet); the Manager stage forwards the timesheet to the Deal Owners.
 * (RAW status — see note on `awaitsMyManagerApproval`.)
 */
export function approveTargetStatus(status?: MonthlyTimesheet['status']): TimesheetStatus {
  return status === TimesheetStatus.PENDING_SVP_MANAGER
    ? TimesheetStatus.CLOSED
    : TimesheetStatus.PENDING_DEAL_OWNERS;
}

/**
 * Label for where the timesheet goes after approval, for confirmation/toast copy. SVP approval closes
 * the timesheet (no downstream recipient), the Manager stage sends it to the Deal Owner.
 */
export function approveDestinationLabel(status: MonthlyTimesheet['status']): string | undefined {
  return status === TimesheetStatus.PENDING_SVP_MANAGER ? undefined : 'Deal Owner';
}
